<template>
  <div class="home">
    <nav class="nav" :class="{ 'nav--solid': scrolled }">
      <span class="nav-brand">Jiamid</span>
      <div class="nav-links">
        <router-link to="/tools" class="nav-link">小工具</router-link>
        <button type="button" class="glass-btn glass-btn--sm nav-login" @click="openLogin">{{ loggedIn ? '进入' : '登录' }}</button>
      </div>
    </nav>

    <header class="hero">
      <div class="hero-bg" aria-hidden="true">
        <PixelWorld v-if="!pixiFailed" preview hide-names username="访客" :players="{}" :connected="false" :interact-enabled="false" @render-failed="onRenderFailed" @render-ready="onRenderReady" />
        <HeroFallback v-if="pixiFailed" />
        <div class="hero-shade" />
      </div>
      <div class="hero-inner">
        <div class="hero-copy reveal">
          <h1 class="hero-title"><span class="hero-thin">Halo</span><span class="hero-bold">&nbsp;Jiamid</span></h1>
          <p class="hero-sub">来探索奇妙的小世界吧~</p>
        </div>
        <div class="hero-actions reveal">
          <button type="button" class="glass-btn glass-btn--lg glass-btn--white btn" @click="openLogin">进入异世界</button>
        </div>
      </div>
    </header>

    <section id="features" class="features reveal">
      <h2 class="sec-title">在像素世界里，你可以…</h2>
      <div class="feat-grid">
        <div v-for="f in features" :key="f.t" class="feat-card reveal" :class="{ 'feat-card--link': f.link }" @click="f.link && $router.push(f.link)">
          <div class="feat-ico">{{ f.i }}</div>
          <h3>{{ f.t }}</h3>
          <p>{{ f.d }}</p>
        </div>
      </div>
      <div class="feat-cta">
        <button type="button" class="glass-btn glass-btn--lg btn" @click="openLogin">{{ loggedIn ? '进入世界' : '登录 / 注册' }}</button>
      </div>
    </section>

    <footer class="icp-footer">
      <span>© Jiamid</span>
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">粤ICP备2021161716号-2</a>
    </footer>

    <LoginModal v-model:visible="showLoginModal" @success="onLoginSuccess" />
  </div>
</template>

<script>
import LoginModal from '@/components/LoginModal.vue'
import PixelWorld from '@/components/PixelWorld.vue'
import HeroFallback from '@/components/HeroFallback.vue'

const FEATURES = [
  { i: '🛠️', t: '实用小工具', d: '时间戳转换、Base64、UUID 等常用开发工具，无需登录。', link: '/tools' },
  { i: '🗺️', t: '到处逛逛', d: '在可以走动的像素地图里瞎逛，说不定有彩蛋。' },
  { i: '💬', t: '和朋友聊天', d: '同一张地图实时聊，冒泡对话，房间号即可加入。' },
  { i: '🎙️', t: '开麦说话', d: '直接开麦，像在同一个房间里。' },
  { i: '👗', t: '换装打扮', d: '头像、服装、配色随心搭，做独一无二的自己。' },
  { i: '🍻', t: '酒馆打牌', d: '推门进酒馆，来一局德州扑克。' },
  { i: '🎁', t: '签到开宝箱', d: '每天签到、开宝箱，收集小惊喜。' },
  { i: '🔮', t: '抽塔罗牌', d: '抽三张牌，让塔罗女巫聊聊你的过去、现在和未来。' },
  { i: '🍟', t: '领麦当劳券', d: '问一句，帮你看活动、领优惠券。' },
  { i: '🧚', t: '无水印解析', d: '把 B 站、抖音的分享链接交给解析小精灵，一键拿到无水印视频。' }
]

export default {
  name: 'Home',
  components: { LoginModal, PixelWorld, HeroFallback },
  data () {
    return {
      features: FEATURES,
      // 不再因 prefers-reduced-motion 退回静态图（静态图里角色不会动）；Pixi 失败时切到 Canvas2D 兜底
      pixiFailed: false,
      showLoginModal: false,
      pendingPath: '',
      username: localStorage.getItem('username') || '',
      loggedIn: !!localStorage.getItem('token'),
      scrolled: false,
      observer: null
    }
  },
  mounted () {
    this.$el.addEventListener('scroll', this.onScroll, { passive: true })
    const items = this.$el.querySelectorAll('.reveal')
    if (typeof IntersectionObserver === 'undefined') {
      items.forEach(el => el.classList.add('in'))
      return
    }
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in')
          this.observer.unobserve(e.target)
        }
      })
    }, { root: this.$el, threshold: 0.15 })
    items.forEach(el => this.observer.observe(el))
  },
  beforeUnmount () {
    this.$el.removeEventListener('scroll', this.onScroll)
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    onRenderFailed () { this.pixiFailed = true },
    onRenderReady () { this.pixiFailed = false },
    onScroll () { this.scrolled = this.$el.scrollTop > 20 },
    scrollTo (id) {
      const el = this.$el.querySelector('#' + id)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    },
    openLogin () {
      if (localStorage.getItem('token')) {
        this.$router.push('/jiamid-world')
        return
      }
      this.pendingPath = ''
      this.showLoginModal = true
    },
    onLoginSuccess ({ username }) {
      this.username = username
      this.loggedIn = true
      this.$router.push(this.pendingPath || '/jiamid-world')
    }
  }
}
</script>

<style scoped>
.home {
  position: relative;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  overflow-x: hidden;
  background: #fff;
  color: #1d1d1f;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'PingFang SC', 'Helvetica Neue', 'Microsoft YaHei', sans-serif;
  -webkit-font-smoothing: antialiased;
  scroll-behavior: smooth;
  touch-action: pan-y;
}

/* Apple/macOS 风格滚动条（触屏(hover:none+pointer:coarse)保留原生 overlay） */
@media not all and (hover: none) and (pointer: coarse) {
  /* 标准属性仅给 Firefox（Chrome 121+ 设置后会忽略 ::-webkit-scrollbar） */
  @supports (-moz-appearance: none) {
    .home {
      scrollbar-width: thin;
      scrollbar-color: rgba(0, 0, 0, 0.28) transparent;
    }
    .home:hover,
    .home:active {
      scrollbar-color: rgba(0, 0, 0, 0.5) transparent;
    }
  }
  .home::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    background: transparent;
  }
  .home::-webkit-scrollbar-track,
  .home::-webkit-scrollbar-corner {
    background: transparent;
  }
  .home::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }
  .home::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.28);
    border: 2px solid transparent; /* 空闲更细 (4px) */
    border-radius: 999px;
    background-clip: padding-box;
    min-height: 40px;
  }
  .home::-webkit-scrollbar-thumb:hover,
  .home::-webkit-scrollbar-thumb:active {
    background-color: rgba(0, 0, 0, 0.5);
    border-width: 1px; /* 悬停变粗 (6px) */
  }
}

/* 导航：毛玻璃 */
.nav {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 max(20px, 6vw);
  background: rgba(255, 255, 255, 0.7);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: none;
  -webkit-backdrop-filter: saturate(180%) blur(20px);
  backdrop-filter: saturate(180%) blur(20px);
  transition: background 0.3s;
}
.nav--solid { background: rgba(255, 255, 255, 0.78); }
#features { scroll-margin-top: 0; }
.nav-brand { font-size: 17px; font-weight: 600; letter-spacing: 0.02em; color: #1d1d1f; }
.nav-links { display: flex; align-items: center; gap: 28px; }
.nav-links a, .nav-link { color: #6e6e73; font-size: 13px; text-decoration: none; transition: color 0.2s; }
.nav-links a:hover, .nav-link:hover { color: #1d1d1f; }
.nav-login { font-size: 13px; }

/* 首屏：像素世界访客预览地图作为全屏动态背景 */
.hero {
  position: relative; overflow: hidden;
  min-height: calc(100vh - 52px); min-height: calc(100dvh - 52px);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; padding: 0 24px 6vh; background: #f5f5f7;
}
.hero-bg { position: absolute; inset: 0; z-index: 0; }
.hero-bg > :deep(.pixel-world) { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
.hero-bg-static { position: absolute; inset: 0; background-color: #f5f5f7; background-size: cover; background-position: center; }
/* 仅极轻顶/底淡入：顶给毛玻璃导航呼吸空间，底衔接 features；中间地图几乎全透 */
.hero-shade { position: absolute; inset: 0; pointer-events: none;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 14%),
    linear-gradient(180deg, transparent 78%, rgba(255,255,255,0.35) 92%, #f5f5f7 100%); }
.hero-inner { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 28px; }
/* 标题区：无磨砂卡片/圆角底，仅文字 + text-shadow 压在地图上可读 */
.hero-copy {
  display: flex; flex-direction: column; align-items: center;
  max-width: min(92vw, 720px);
  padding: 0;
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
}
.hero-title, .sec-title { text-wrap: balance; }
.hero-title {
  font-size: clamp(48px, 10vw, 112px); line-height: 1.05; letter-spacing: -0.02em; margin: 0 0 16px;
  color: #1d1d1f;
  text-shadow:
    0 1px 2px rgba(255, 255, 255, 0.95),
    0 0 18px rgba(255, 255, 255, 0.8),
    0 2px 12px rgba(255, 255, 255, 0.55),
    0 1px 3px rgba(0, 0, 0, 0.18);
}
.hero-thin { font-weight: 300; } .hero-bold { font-weight: 800; }
.hero-sub {
  font-size: clamp(16px, 2.2vw, 24px); line-height: 1.4; color: #1d1d1f; margin: 0; font-weight: 500;
  text-shadow:
    0 1px 2px rgba(255, 255, 255, 0.95),
    0 0 14px rgba(255, 255, 255, 0.75),
    0 1px 3px rgba(0, 0, 0, 0.15);
}
.hero-actions { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; justify-content: center; }

/* 特性 */
.features { padding: 14vh max(24px, 8vw) 10vh; background: #f5f5f7; color: #1d1d1f; }
.sec-title { font-size: clamp(32px, 5.5vw, 60px); font-weight: 700; text-align: center; margin: 0 0 48px; letter-spacing: -0.01em; color: #1d1d1f; }
.feat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; max-width: 1100px; margin: 0 auto; }
.feat-card, .glass-card {
  background: rgba(255,255,255,0.82); border: 1px solid rgba(0,0,0,0.06); border-radius: 24px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06);
  -webkit-backdrop-filter: blur(24px) saturate(180%); backdrop-filter: blur(24px) saturate(180%);
}
.feat-card { padding: 28px 24px; }
.feat-card--link { cursor: pointer; transition: all 0.3s ease; }
.feat-card--link:hover { transform: translateY(-4px); box-shadow: 0 2px 4px rgba(0,0,0,0.06), 0 12px 32px rgba(0,0,0,0.1); }
.feat-card:nth-child(2) { transition-delay: .08s; } .feat-card:nth-child(3) { transition-delay: .16s; }
.feat-ico { font-size: 36px; margin-bottom: 12px; }
.feat-card h3 { font-size: 21px; margin: 0 0 8px; font-weight: 600; color: #1d1d1f; }
.feat-card p { margin: 0; color: #6e6e73; font-size: 15px; line-height: 1.5; }
.glass-card { padding: clamp(24px, 3vw, 44px); }

/* 按钮：浅色背景下的扁平 liquid glass（仅首页作用域） */
.btn { font-size: 17px; }
.home .glass-btn {
  --gb-fg: #0066cc;
  --gb-bg: rgba(0, 113, 227, 0.12);
  --gb-bg-hover: rgba(0, 113, 227, 0.2);
  --gb-edge: rgba(0, 113, 227, 0.28);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.home .glass-btn::before { display: none; }
.home .glass-btn:hover:not(:disabled) { --gb-edge: rgba(0, 113, 227, 0.4); }
.home .glass-btn--ghost {
  --gb-fg: #1d1d1f;
  --gb-bg: rgba(255, 255, 255, 0.6);
  --gb-bg-hover: rgba(255, 255, 255, 0.85);
  --gb-edge: rgba(0, 0, 0, 0.12);
}
.home .glass-btn--ghost:hover:not(:disabled) { --gb-edge: rgba(0, 0, 0, 0.2); }
.home .glass-btn:focus-visible { outline-color: #0071e3; }

/* 首屏「进入异世界」：Apple liquid glass —— 极低不透明白色填充 + 强模糊饱和，露出地图；扁平 */
.home .glass-btn.glass-btn--white:not(.glass-btn--ghost) {
  --gb-fg: #1d1d1f;
  --gb-bg: rgba(255, 255, 255, 0.16);
  --gb-bg-hover: rgba(255, 255, 255, 0.24);
  --gb-edge: rgba(255, 255, 255, 0.6);
  color: #1d1d1f;
  font-weight: 600;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.55), 0 1px 1px rgba(255, 255, 255, 0.6);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    inset 0 0 0 1px rgba(255, 255, 255, 0.18),
    0 4px 16px rgba(0, 0, 0, 0.07);
  -webkit-backdrop-filter: blur(28px) saturate(200%);
  backdrop-filter: blur(28px) saturate(200%);
}
.home .glass-btn.glass-btn--white:hover:not(:disabled) { --gb-edge: rgba(255, 255, 255, 0.75); }
.home .glass-btn.glass-btn--white:focus-visible { outline-color: #1d1d1f; }

.feat-cta { text-align: center; margin-top: 48px; }

.icp-footer {
  display: flex; justify-content: center; gap: 16px; flex-wrap: wrap;
  padding: 24px; font-size: 12px; color: #6e6e73; background: #fff; border-top: 1px solid rgba(0,0,0,0.08);
}
.icp-footer a { color: #6e6e73; text-decoration: none; }
.icp-footer a:hover { text-decoration: underline; }

/* 滚动渐显 */
.reveal { opacity: 0; transform: translateY(32px); transition: opacity 0.9s ease, transform 0.9s ease; }
.reveal.in { opacity: 1; transform: none; }

@media (max-width: 768px) {
  .feat-grid { grid-template-columns: 1fr; }
  .nav-links { gap: 16px; }
}
@media (prefers-reduced-motion: reduce) {
  .reveal { opacity: 1; transform: none; transition: none; }
  .home { scroll-behavior: auto; }
}
</style>
